<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisFilterBar from 'dashboard/components-next/LaisFilterBar.vue';
import OfferClientSearch from './OfferClientSearch.vue';

defineProps({
  searchTerm: { type: String, default: '' },
  clientSearchOptions: { type: Array, default: () => [] },
  offerKindFilterLabel: { type: String, default: '' },
  paymentFilterLabel: { type: String, default: '' },
  activityFilterLabel: { type: String, default: '' },
  contractFilterLabel: { type: String, default: '' },
  documentationFilterLabel: { type: String, default: '' },
  rentProgressPhase: { type: String, default: '' },
  propertyIdFilter: { type: String, default: '' },
  businessContext: { type: String, default: 'all' },
  businessContextOptions: { type: Array, default: () => [] },
  negotiationStatus: { type: String, default: 'all' },
  negotiationStatusOptions: { type: Array, default: () => [] },
  showNegotiationStatus: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:searchTerm',
  'openOfferTypeDrawer',
  'openPaymentKindDrawer',
  'openActivityDrawer',
  'openContractFiltersDrawer',
  'openDocumentationFiltersDrawer',
  'selectClientSearchOption',
  'applyFilters',
  'clearPropertyFilter',
  'selectBusinessContext',
  'selectNegotiationStatus',
]);

const openOfferTypeDrawer = () => emit('openOfferTypeDrawer');
const openPaymentKindDrawer = () => emit('openPaymentKindDrawer');
const openActivityDrawer = () => emit('openActivityDrawer');
const openContractFiltersDrawer = () => emit('openContractFiltersDrawer');
const openDocumentationFiltersDrawer = () =>
  emit('openDocumentationFiltersDrawer');
const selectClientSearchOption = option =>
  emit('selectClientSearchOption', option);
const applyFilters = () => emit('applyFilters');
const clearPropertyFilter = () => emit('clearPropertyFilter');
const selectBusinessContext = event =>
  emit('selectBusinessContext', event.target.value);
const selectNegotiationStatus = event =>
  emit('selectNegotiationStatus', event.target.value);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisFilterBar>
    <div class="flex min-w-[170px] flex-1 flex-col gap-1 sm:max-w-[200px]">
      <label
        class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
      >
        Finalidade
      </label>
      <select
        class="h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text transition-colors hover:border-lais-primary"
        :value="businessContext"
        aria-label="Filtrar por finalidade"
        @change="selectBusinessContext"
      >
        <option
          v-for="option in businessContextOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>
    <div
      v-if="showNegotiationStatus"
      class="flex min-w-[170px] flex-1 flex-col gap-1 sm:max-w-[200px]"
    >
      <label
        class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
      >
        Situação
      </label>
      <select
        class="h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text transition-colors hover:border-lais-primary"
        :value="negotiationStatus"
        aria-label="Filtrar por situação da negociação"
        @change="selectNegotiationStatus"
      >
        <option
          v-for="option in negotiationStatusOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>
    <div class="flex min-w-[170px] flex-1 flex-col gap-1 sm:max-w-[200px]">
      <label
        class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
      >
        Tipo de proposta
      </label>
      <button
        type="button"
        class="flex h-10 w-full items-center justify-between gap-2 rounded-lais border border-lais-border bg-white px-3 text-left text-[14px] text-lais-text transition-colors hover:border-lais-primary"
        @click="openOfferTypeDrawer"
      >
        <span class="truncate">{{ offerKindFilterLabel || 'Todas' }}</span>
        <span class="i-lucide-list-filter size-4 shrink-0" aria-hidden="true" />
      </button>
    </div>
    <div class="flex min-w-[190px] flex-1 flex-col gap-1 sm:max-w-[220px]">
      <label
        class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
      >
        Forma de pagamento
      </label>
      <button
        type="button"
        class="flex h-10 w-full items-center justify-between gap-2 rounded-lais border border-lais-border bg-white px-3 text-left text-[14px] text-lais-text transition-colors hover:border-lais-primary"
        @click="openPaymentKindDrawer"
      >
        <span class="truncate">{{ paymentFilterLabel }}</span>
        <span
          class="i-lucide-sliders-horizontal size-4 shrink-0"
          aria-hidden="true"
        />
      </button>
    </div>
    <div class="flex min-w-[170px] flex-1 flex-col gap-1 sm:max-w-[220px]">
      <label
        class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
      >
        Tempo de atividade
      </label>
      <button
        type="button"
        class="flex h-10 w-full items-center justify-between gap-2 rounded-lais border border-lais-border bg-white px-3 text-left text-[14px] text-lais-text transition-colors hover:border-lais-primary"
        @click="openActivityDrawer"
      >
        <span>{{ activityFilterLabel || 'Últimos períodos' }}</span>
        <span class="i-lucide-calendar-days size-4" aria-hidden="true" />
      </button>
    </div>
    <div
      v-if="rentProgressPhase === 'contract'"
      class="flex min-w-[210px] flex-1 flex-col gap-1 sm:max-w-[260px]"
    >
      <label
        class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
      >
        Contratos
      </label>
      <button
        type="button"
        class="flex h-10 w-full items-center justify-between gap-2 rounded-lais border border-lais-border bg-white px-3 text-left text-[14px] text-lais-text transition-colors hover:border-lais-primary"
        @click="openContractFiltersDrawer"
      >
        <span class="truncate">{{ contractFilterLabel }}</span>
        <span class="i-lucide-filter size-4 shrink-0" aria-hidden="true" />
      </button>
    </div>
    <div
      v-if="rentProgressPhase === 'documentation'"
      class="flex min-w-[210px] flex-1 flex-col gap-1 sm:max-w-[260px]"
    >
      <label
        class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
      >
        Documentações
      </label>
      <button
        type="button"
        class="flex h-10 w-full items-center justify-between gap-2 rounded-lais border border-lais-border bg-white px-3 text-left text-[14px] text-lais-text transition-colors hover:border-lais-primary"
        @click="openDocumentationFiltersDrawer"
      >
        <span class="truncate">{{ documentationFilterLabel }}</span>
        <span class="i-lucide-filter size-4 shrink-0" aria-hidden="true" />
      </button>
    </div>
    <OfferClientSearch
      :model-value="searchTerm"
      :options="clientSearchOptions"
      @update:model-value="emit('update:searchTerm', $event)"
      @select="selectClientSearchOption"
      @clear="applyFilters"
      @submit="applyFilters"
    />
    <div
      v-if="propertyIdFilter"
      class="flex min-w-[160px] flex-1 flex-col gap-1 sm:max-w-[220px]"
    >
      <label
        class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
      >
        Imóvel
      </label>
      <button
        type="button"
        class="flex h-10 w-full items-center justify-between gap-2 rounded-lais border border-lais-border bg-white px-3 text-left text-[14px] text-lais-text transition-colors hover:border-lais-primary"
        title="Limpar filtro de imóvel"
        @click="clearPropertyFilter"
      >
        <span class="truncate">Imóvel #{{ propertyIdFilter }}</span>
        <span class="i-lucide-x size-4 shrink-0" aria-hidden="true" />
      </button>
    </div>
  </LaisFilterBar>
</template>
